import { createContext, useContext, useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { installSessionSafety, sessionChangedError } from './sessionSafety';
import SessionChangedNotice from './SessionChangedNotice';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(() => localStorage.getItem('token'));
    const [loading, setLoading] = useState(true);
    const [sessionChanged, setSessionChanged] = useState(false);
    const sessionRef = useRef({ token, version: 0, blocked: false });
    const authAttemptRef = useRef(0);

    const invalidate = useCallback(() => {
        if (!sessionRef.current.blocked) {
            sessionRef.current = { ...sessionRef.current, blocked: true };
            setSessionChanged(true);
        }
    }, []);

    const checkSession = useCallback(() => {
        if (sessionRef.current.blocked) return false;
        if (localStorage.getItem('token') !== sessionRef.current.token) {
            invalidate();
            return false;
        }
        return true;
    }, [invalidate]);

    useLayoutEffect(() => {
        const dispose = installSessionSafety({ client: axios, api: API,
            getSession: () => sessionRef.current, checkSession, invalidate });
        const onStorage = event => {
            if (event.storageArea !== localStorage || (event.key !== 'token' && event.key !== null)) return;
            // Also catch a switch away and back before this tab processes the event.
            if (event.newValue !== sessionRef.current.token) invalidate();
            else checkSession();
        };
        const onVisible = () => { if (document.visibilityState === 'visible') checkSession(); };
        window.addEventListener('storage', onStorage);
        window.addEventListener('focus', checkSession);
        document.addEventListener('visibilitychange', onVisible);
        checkSession();
        return () => {
            dispose();
            window.removeEventListener('storage', onStorage);
            window.removeEventListener('focus', checkSession);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [checkSession, invalidate]);

    const logout = useCallback(() => {
        // A stale tab must never remove the other tab's valid session.
        if (!checkSession()) return;
        localStorage.removeItem('token');
        sessionRef.current = { token: null, version: sessionRef.current.version + 1, blocked: false };
        delete axios.defaults.headers.common['Authorization'];
        setToken(null);
        setUser(null);
    }, [checkSession]);

    useEffect(() => {
        let active = true;
        const version = sessionRef.current.version;
        if (token && checkSession()) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            const fetchUser = async () => {
                try {
                    const response = await axios.get(`${API}/auth/me`);
                    if (active && version === sessionRef.current.version && checkSession()) setUser(response.data);
                } catch (error) {
                    if (active && version === sessionRef.current.version && checkSession()) {
                        if (error.response?.status !== 401) console.error('Failed to fetch user');
                        logout();
                    }
                } finally {
                    if (active) setLoading(false);
                }
            };
            fetchUser();
        } else {
            setLoading(false);
        }
        return () => { active = false; };
    }, [token, checkSession, logout]);

    const establishSession = async (path, payload) => {
        if (!checkSession()) throw sessionChangedError();
        const version = sessionRef.current.version;
        const attempt = ++authAttemptRef.current;
        const response = await axios.post(`${API}/auth/${path}`, payload);
        if (attempt !== authAttemptRef.current || version !== sessionRef.current.version || !checkSession()) {
            throw sessionChangedError();
        }
        const { access_token, user: nextUser } = response.data;
        localStorage.setItem('token', access_token);
        sessionRef.current = { token: access_token, version: version + 1, blocked: false };
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        setToken(access_token);
        setUser(nextUser);
        return nextUser;
    };

    const login = (email, password) => establishSession('login', { email, password });
    const register = userData => establishSession('register', userData);

    return (
        <AuthContext.Provider value={{ user, token, loading, login, register, logout }}>
            {children}
            {sessionChanged && <SessionChangedNotice />}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
